import type { User } from '@piccolohealth/pbs-common';
import { ChakraV3, Pagination, ScrollArea, Spin } from '@piccolohealth/ui';
import { P } from '@piccolohealth/util';
import React from 'react';
import { PiccoloError } from '../../components/generic/PiccoloError';
import { useParticipantAuditTrailEntriesQuery } from '../../graphql/hooks/useParticipantQuery';
import { useAppContext } from '../../hooks/useAppContext';
import { ParticipantTimelineItem } from './ParticipantTimelineItem';

interface Props {
  participantId: string;
}

export const ParticipantTimelineContents = (props: Props) => {
  const { participantId } = props;
  const { organization } = useAppContext();

  const { isLoading, error, data, pagination } = useParticipantAuditTrailEntriesQuery({
    organizationId: organization.id,
    participantId,
    request: {
      pagination: {
        limit: 10,
      },
    },
  });

  const entries = data?.organization?.participant?.entries.results ?? [];

  const contents = P.run(() => {
    if (isLoading) {
      return <Spin />;
    }

    if (error) {
      return <PiccoloError error={error} />;
    }

    if (P.isEmpty(entries)) {
      return <ChakraV3.Result h='full' status='info' title='No history found for participant' />;
    }

    return entries.map((entry) => {
      return (
        <ParticipantTimelineItem
          key={entry.id}
          timestamp={entry.timestamp}
          message={entry.message}
          user={entry.user as User}
          payload={entry.payload}
        />
      );
    });
  });

  return (
    <ChakraV3.Stack w='lg' bg='white' gap={0} shadow='2xl' rounded='xl'>
      <ChakraV3.HStack px={6} py={3} borderBottomWidth='1px'>
        <ChakraV3.Text fontSize='xl' fontWeight='bold'>
          History
        </ChakraV3.Text>
      </ChakraV3.HStack>
      <ScrollArea minH='sm' maxH='2xl' pb={2}>
        {contents}
      </ScrollArea>

      {pagination && (
        <ChakraV3.HStack px={2} py={2} borderTopWidth='1px'>
          <ChakraV3.Spacer />
          <Pagination {...pagination} />
        </ChakraV3.HStack>
      )}
    </ChakraV3.Stack>
  );
};
